import type { RoomResponse, RoomStatus } from '../types/auth';

/**
 * CORE-842 — decide what a /room deep-link visitor should actually see.
 *
 * The announcement screen used to collapse every un-openable state into one
 * technical dead-end (the «!» icon + raw backend `error` string + «НАЗАД»).
 * The ticket replaces that with a single approved user-facing fallback
 * («Этот эфир сейчас недоступен»), while keeping the ONE recoverable state
 * that is not an error — an anonymous visitor with a valid link who just
 * needs to sign in (CORE-239).
 *
 * `'signin'`      — recoverable: valid link, no session yet → offer sign-in.
 * `'unavailable'` — covered dead-end: show the approved fallback screen.
 * `'retry'`       — recoverable: the load blinked (network / timeout / 5xx),
 *                   the room may still be there → keep it retryable, never
 *                   collapse a transient failure into the terminal fallback.
 * `'ok'`          — render the normal announcement.
 *
 * Kept as a pure function so the branch decision is unit-testable without
 * rendering the screen (jest runs in a node env here).
 */
export type RoomAvailability = 'ok' | 'signin' | 'unavailable' | 'retry';

export interface RoomAvailabilityInput {
  /** The room fetch failed (threw) or resolved to no room. */
  loadFailed: boolean;
  isAuthenticated: boolean;
  /** Raw backend status once the room loaded; null when it didn't. */
  status: RoomStatus | null;
  /** Live-practices feature flag (AMB-231 direct-entry gate). */
  livePracticesEnabled: boolean;
  /** Viewer is the room's host (Quick Room creators bypass the flag gate). */
  isHost: boolean;
  /**
   * CORE-842 — the load failed with a *transient* error (network drop,
   * timeout, aborted request, 5xx/429). Optional; defaults to `false` so the
   * announcement caller (which can't hit a mid-load network blip the same way)
   * is unaffected. When `true`, a failed load stays recoverable (`'retry'`)
   * instead of collapsing into the terminal fallback — a blinked connection
   * must not become a dead-end for the room-session route.
   */
  loadFailedTransient?: boolean;
}

export function resolveRoomAvailability(input: RoomAvailabilityInput): RoomAvailability {
  // Couldn't load the room. A *transient* failure (network / timeout / 5xx)
  // may well succeed on retry — keep it recoverable and never paint the
  // permanent «эфир недоступен» dead-end over a blinked connection (CORE-842).
  if (input.loadFailed || input.status === null) {
    if (input.loadFailedTransient) return 'retry';
    // Terminal failure. For a signed-in user this is a genuine dead-end
    // (deleted / expired / missing). For an anonymous visitor it is usually
    // just an auth-gated endpoint 404 for a link that IS valid — recoverable
    // via sign-in, so it must NOT be swallowed by the fallback (CORE-239).
    return input.isAuthenticated ? 'unavailable' : 'signin';
  }

  // Soft-cancel (CORE-833): BE returns 200 + status=Cancelled with a fully
  // populated room, so without this it would render as a normal announcement.
  if (input.status === 'Cancelled') return 'unavailable';

  // AMB-231 — non-host deep-link visitors are blocked when the flag is off.
  if (!input.livePracticesEnabled && !input.isHost) return 'unavailable';

  return 'ok';
}

/**
 * CORE-842 — is a failed room fetch *transient* (keep it recoverable) rather
 * than a definitive dead-end (safe to surface the approved fallback)?
 *
 * The room-session screen's `roomError` catches everything the load can throw:
 * a real «no such room» (404 / 410), a forbidden room (403 / 401), AND a
 * blinked network / timed-out / aborted / 5xx request. Only the first kind may
 * paint the terminal «эфир недоступен» screen; a transient failure has to stay
 * retryable so a dropped connection doesn't trap the user on a dead-end.
 *
 *   Terminal  → an HTTP 4xx that won't fix itself (404 / 410 / 403 / 401 /
 *               400 / 409 …). Feed to `resolveRoomAvailability` as a normal
 *               (non-transient) load failure.
 *   Transient → no HTTP `status` at all (fetch threw: DNS / TCP / TLS / abort /
 *               timeout), or a 5xx / 429 / 408 server hiccup.
 */
export function isTransientRoomLoadError(err: unknown): boolean {
  const status = (err as { status?: unknown } | null | undefined)?.status;
  // No HTTP status → the request never got an answer (network / abort /
  // timeout). Retryable.
  if (typeof status !== 'number') return true;
  // Server-side hiccups that a retry can clear.
  if (status >= 500 || status === 429 || status === 408) return true;
  // Any other status (the 4xx family) is a definitive answer — terminal.
  return false;
}

/**
 * CORE-1058 — is a failed room fetch the *expected* «room is gone» terminal
 * rather than a fault worth a Sentry event?
 *
 * The by-code route `GET /api/watch-parties/{code}` is Joinable-filtered
 * (Status != Ended, the CORE-547 invite-entry gate), so a room that has just
 * *ended* answers 404 to it — and a deleted / expired room answers 404/410
 * too. That is the normal end-of-session state («сессия завершена», surfaced
 * as the `'unavailable'` fallback), NOT a client error. Reporting it flooded
 * Sentry issue REACT-NATIVE-2Y and buried the genuine `live_room_session`
 * failures underneath. 5xx / 403 / 401 / network drops are still real faults.
 */
export function isExpectedRoomGone(err: unknown): boolean {
  const status = (err as { status?: unknown } | null | undefined)?.status;
  return status === 404 || status === 410;
}

/**
 * CORE-1058 — should a failed room load reach Sentry?
 *
 *   • Anonymous viewer — a 404 on an auth-gated endpoint is the expected
 *     «valid link, just sign in» state (CORE-239), never an error. The
 *     room-session screen already only logged for signed-in users; keep that.
 *   • Signed-in viewer — report everything EXCEPT the expected «room gone»
 *     404/410 (ended / deleted / expired room). A finished session answering
 *     404 to the Joinable-filtered by-code route is not a fault (AC1); a 5xx,
 *     403 or network drop still is and must keep flowing (AC3).
 */
export function shouldReportRoomLoadError(err: unknown, isAuthenticated: boolean): boolean {
  if (!isAuthenticated) return false;
  if (isExpectedRoomGone(err)) return false;
  return true;
}

/**
 * CORE-1459 — can the post-`partyEnded` canonical refetch (CORE-523,
 * `GET /watch-parties/id/{id}`) answer this viewer at all?
 *
 * The BE `GetById` 404s any non-Public room for a request without a JWT, and
 * the by-code route is Joinable-filtered, so for an anonymous guest of a
 * «Только по ссылке» room (Visibility=Private, the vast majority) no route
 * returns the ended room: the refetch is a guaranteed 404 that only flooded
 * Sentry (REACT-NATIVE-4B). Mirrors the BE gate; `visibility` is absent on an
 * older BE, fall back to the deprecated `isPublic` mirror.
 */
export function canRefetchEndedRoom(
  room: Pick<RoomResponse, 'visibility' | 'isPublic'> | null | undefined,
  isAuthenticated: boolean,
): boolean {
  if (isAuthenticated) return true;
  if (!room) return false;
  return (room.visibility ?? (room.isPublic ? 'Public' : 'Private')) === 'Public';
}
